import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withKeys as _withKeys, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-label"]
const _hoisted_2 = { class: "ck__box" }
const _hoisted_3 = { class: "ck__text" }
const _hoisted_4 = ["href"]
const _hoisted_5 = ["href"]
const _hoisted_6 = { class: "ck__actions" }
const _hoisted_7 = ["aria-label"]
const _hoisted_8 = { class: "ck-modal__box" }
const _hoisted_9 = { class: "ck-modal__title" }
const _hoisted_10 = { class: "ck-modal__lead" }
const _hoisted_11 = { class: "ck-row" }
const _hoisted_12 = { class: "ck-row__fixed" }
const _hoisted_13 = { class: "ck-row" }
const _hoisted_14 = ["aria-checked", "aria-label"]
const _hoisted_15 = { class: "ck-row" }
const _hoisted_16 = ["aria-checked", "aria-label"]
const _hoisted_17 = { class: "ck__actions" }

import { computed, onMounted, reactive, ref } from 'vue';

const KEY = 'helfer-cookie-consent';
const VERSION = 2;
const MAX_AGE = 182 * 86400000;
type Consent = { necessary: true; analytics: boolean; ads: boolean; ts: number; v: number };

export default /*@__PURE__*/_defineComponent({
  __name: 'CookieConsentBanner',
  setup(__props) {

// Cookie consent for the public web pages (Irish DPC guidance, GDPR + ePrivacy):
// nothing optional runs before a choice; «Reject all» is as easy as «Accept all»;
// analytics and advertising are separate and off by default; the choice is kept
// for 6 months, then asked again; window.openCookieSettings() reopens it.
// Trackers must wait for consent: read window.helferConsent or listen for the
// 'helfer-consent' event ({ analytics, ads }).
const visible = ref(false);
const settingsOpen = ref(false);
const draft = reactive({ analytics: false, ads: false });

const TEXT = {
  en: {
    label: 'Cookie consent', heading: 'Cookies.', body: 'We use necessary cookies to make this site work. With your permission we would also use analytics cookies to improve it and advertising cookies (Meta, Google) to measure our ads. Nothing optional is set until you choose.',
    cookies: 'Cookie policy', privacy: 'Privacy', reject: 'Reject all', settings: 'Settings', accept: 'Accept all', save: 'Save choices',
    settingsTitle: 'Cookie settings', settingsLead: 'Choose what we may use. You can change it any time with «Cookie settings» at the bottom of the page.',
    necessary: 'Necessary', necessaryHint: 'Keep the site working and remember this choice.', alwaysOn: 'Always on',
    analytics: 'Analytics', analyticsHint: 'Count visits and see which pages help.', ads: 'Advertising', adsHint: 'Meta Pixel and Google Ads measure which ads bring visitors.',
  },
  ru: {
    label: 'Согласие на cookies', heading: 'Cookies.', body: 'Необходимые cookies нужны для работы сайта. С вашего разрешения мы также используем аналитические cookies, чтобы улучшать сайт, и рекламные (Meta, Google), чтобы оценивать нашу рекламу. Пока вы не выбрали, ничего необязательного не включается.',
    cookies: 'Политика cookies', privacy: 'Конфиденциальность', reject: 'Отклонить все', settings: 'Настроить', accept: 'Принять все', save: 'Сохранить выбор',
    settingsTitle: 'Настройки cookies', settingsLead: 'Выберите, что нам можно использовать. Изменить выбор можно в любой момент ссылкой «Настройки cookies» внизу страницы.',
    necessary: 'Необходимые', necessaryHint: 'Обеспечивают работу сайта и запоминают этот выбор.', alwaysOn: 'Всегда включены',
    analytics: 'Аналитика', analyticsHint: 'Подсчёт посещений и полезных страниц.', ads: 'Реклама', adsHint: 'Meta Pixel и Google Ads оценивают, какая реклама приводит посетителей.',
  },
};
const lang = computed(() => {
  const l = (typeof document !== 'undefined' && document.documentElement.lang) || (typeof navigator !== 'undefined' && navigator.language) || 'en';
  return /^(ru|uk)/i.test(l) ? 'ru' : 'en';
});
const tx = computed(() => TEXT[lang.value]);
// Cafe Local pages point to Cafe Local's documents; the Helfer pages keep theirs.
const docs = computed(() => (typeof window !== 'undefined' && window.location.pathname.startsWith('/cafe-local')
  ? { cookies: '/cafe-local/cookies', privacy: '/cafe-local/privacy' }
  : { cookies: '/cookies', privacy: '/privacy' }));

function publish(c: Consent | null) {
  const w = window as unknown as { helferConsent?: Consent | null };
  w.helferConsent = c;
  window.dispatchEvent(new CustomEvent('helfer-consent', { detail: c }));
}
function read(): Consent | null {
  try {
    const c = JSON.parse(window.localStorage.getItem(KEY) || 'null');
    if (!c || c.v !== VERSION || !(Date.now() - Number(c.ts) < MAX_AGE)) return null;
    return c;
  } catch { return null; }
}
function choose(analytics: boolean, ads: boolean) {
  const c: Consent = { necessary: true, analytics, ads, ts: Date.now(), v: VERSION };
  try { window.localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* private mode: ask again next time */ }
  visible.value = false;
  settingsOpen.value = false;
  publish(c);
}
function openSettings() {
  const c = read();
  draft.analytics = !!c?.analytics;
  draft.ads = !!c?.ads;
  visible.value = false;
  settingsOpen.value = true;
}

onMounted(() => {
  (window as unknown as { openCookieSettings?: () => void }).openCookieSettings = openSettings;
  // Never in the desktop app, nor on the venue's own screens (guest menu, staff):
  // they keep only what is strictly needed on the device and have no trackers.
  if ((window as unknown as { __CAFE_DESKTOP_RUNTIME__?: unknown }).__CAFE_DESKTOP_RUNTIME__) return;
  if (/^\/(v2|table|admin|kitchen|storefront|desktop-entry|login|old)(\/|$)/i.test(window.location.pathname)) return;
  const c = read();
  publish(c);
  if (!c) visible.value = true;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    (visible.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "ck",
          role: "region",
          "aria-label": tx.value.label
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createElementVNode("p", _hoisted_3, [
              _createElementVNode("strong", null, _toDisplayString(tx.value.heading), 1),
              _createTextVNode(" " + _toDisplayString(tx.value.body) + " ", 1),
              _createElementVNode("a", {
                href: docs.value.cookies
              }, _toDisplayString(tx.value.cookies), 9, _hoisted_4),
              _cache[8] || (_cache[8] = _createTextVNode(" · ", -1)),
              _createElementVNode("a", {
                href: docs.value.privacy
              }, _toDisplayString(tx.value.privacy), 9, _hoisted_5)
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (choose(false, false)))
              }, _toDisplayString(tx.value.reject), 1),
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: openSettings
              }, _toDisplayString(tx.value.settings), 1),
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: _cache[1] || (_cache[1] = ($event: any) => (choose(true, true)))
              }, _toDisplayString(tx.value.accept), 1)
            ])
          ])
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    (settingsOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: "ck-modal",
          role: "dialog",
          "aria-modal": "true",
          "aria-label": tx.value.settingsTitle,
          onKeydown: _cache[7] || (_cache[7] = _withKeys(($event: any) => (settingsOpen.value = false), ["esc"]))
        }, [
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("h2", _hoisted_9, _toDisplayString(tx.value.settingsTitle), 1),
            _createElementVNode("p", _hoisted_10, _toDisplayString(tx.value.settingsLead), 1),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("span", null, [
                _createElementVNode("strong", null, _toDisplayString(tx.value.necessary), 1),
                _createElementVNode("small", null, _toDisplayString(tx.value.necessaryHint), 1)
              ]),
              _createElementVNode("span", _hoisted_12, _toDisplayString(tx.value.alwaysOn), 1)
            ]),
            _createElementVNode("div", _hoisted_13, [
              _createElementVNode("span", null, [
                _createElementVNode("strong", null, _toDisplayString(tx.value.analytics), 1),
                _createElementVNode("small", null, _toDisplayString(tx.value.analyticsHint), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "ck-switch",
                role: "switch",
                "aria-checked": draft.analytics ? 'true' : 'false',
                "aria-label": tx.value.analytics,
                onClick: _cache[2] || (_cache[2] = ($event: any) => (draft.analytics = !draft.analytics))
              }, null, 8, _hoisted_14)
            ]),
            _createElementVNode("div", _hoisted_15, [
              _createElementVNode("span", null, [
                _createElementVNode("strong", null, _toDisplayString(tx.value.ads), 1),
                _createElementVNode("small", null, _toDisplayString(tx.value.adsHint), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "ck-switch",
                role: "switch",
                "aria-checked": draft.ads ? 'true' : 'false',
                "aria-label": tx.value.ads,
                onClick: _cache[3] || (_cache[3] = ($event: any) => (draft.ads = !draft.ads))
              }, null, 8, _hoisted_16)
            ]),
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: _cache[4] || (_cache[4] = ($event: any) => (choose(false, false)))
              }, _toDisplayString(tx.value.reject), 1),
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (choose(draft.analytics, draft.ads)))
              }, _toDisplayString(tx.value.save), 1),
              _createElementVNode("button", {
                type: "button",
                class: "ck__btn",
                onClick: _cache[6] || (_cache[6] = ($event: any) => (choose(true, true)))
              }, _toDisplayString(tx.value.accept), 1)
            ])
          ])
        ], 40, _hoisted_7))
      : _createCommentVNode("", true)
  ]))
}
}

})